import type Konva from "konva";
import { useElementSize, useFullscreen } from "@vueuse/core";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { BASE_TELESTRATION_STAGE_SIZE } from "~/telestrations/constants/baseTelestrationStageSize";
import { calculateScaledVideoDimensions } from "~/telestrations/helpers/calculateScaledVideoDimensions";

import { getSVGStringFromKonvaStage } from "../helpers/getSVGStringFromKonvaStage";

export const useCanvasStore = defineStore("canvas", () => {
  const containerRef = ref<HTMLDivElement | null>(null);
  const konvaStageRef = ref<Konva.Stage | null>(null);
  const containerSize = useElementSize(containerRef, { width: BASE_TELESTRATION_STAGE_SIZE.width, height: BASE_TELESTRATION_STAGE_SIZE.height });

  const canvasSize = ref({
    // TODO: We can remove .value and it will become reactive and sync the container size
    // However it has some issues with the fullscreen and it very depends on the container element
    // Sometimes we can end up in the infinite loop. So we need to find a better solution
    width: containerSize.width,
    height: containerSize.height,
  });

  const fullscreenState = useFullscreen(containerRef);

  const stageConfig = computed<Konva.StageConfig>(() => {
    const { offsetY, offsetX, scale } = calculateScaledVideoDimensions(
      containerSize.width.value,
      containerSize.height.value,
      BASE_TELESTRATION_STAGE_SIZE.width,
      BASE_TELESTRATION_STAGE_SIZE.height
    );
    return {
      width: canvasSize.value.width,
      height: canvasSize.value.height,
      offsetX: -offsetX / scale,
      offsetY: -offsetY / scale,
      scaleX: scale,
      scaleY: scale,
    };
  });

  function getKonvaStage(): Konva.Stage | null {
    return konvaStageRef.value?.getStage() || null;
  }

  function getCurrentSVGString() {
    const stage = getKonvaStage();

    if (!stage) return null;

    return getSVGStringFromKonvaStage(stage);
  }

  return {
    stageConfig,
    containerRef,
    konvaStageRef,
    canvasSize,
    fullscreenState,
    getKonvaStage,
    getCurrentSVGString,
  };
});
